/**
 * Carregador lazy do Facebook JS SDK + helper para Embedded Signup.
 *
 * Uso:
 *   import metaSdk from '@/services/metaSdk';
 *
 *   const config = await metaSdk.fetchConfig();
 *   if (config.configurado) {
 *     await metaSdk.init(config.app_id, config.graph_version);
 *     const result = await metaSdk.startEmbeddedSignup(config.config_id);
 *     // result = { code, sessionInfo? }
 *   }
 *
 * Notas:
 *   - `App ID` e `Configuration ID` vêm do backend (GET /canal/whatsapp/config)
 *     pra evitar duplicar segredo no frontend.
 *   - O SDK só é carregado quando a tela de canais é aberta (lazy).
 */

import axios from 'axios';

const SDK_SRC = 'https://connect.facebook.net/pt_BR/sdk.js';
let sdkPromise = null;

function carregarSdk(version = 'v20.0') {
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise((resolve, reject) => {
    if (typeof window === 'undefined') return reject(new Error('window indisponível'));
    if (window.FB) return resolve(window.FB);

    // Stub init obrigatório pelo SDK
    window.fbAsyncInit = function fbAsyncInit() {
      window.FB.init({
        appId: window.__metaAppId,
        cookie: true,
        xfbml: false,
        version,
      });
      resolve(window.FB);
    };

    const script = document.createElement('script');
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';
    script.src = SDK_SRC;
    script.onerror = () => reject(new Error('Falha ao carregar Facebook SDK'));
    document.head.appendChild(script);
    return null;
  });
  return sdkPromise;
}

export default {
  /**
   * Busca config pública (App ID + Configuration ID + status) do backend.
   */
  async fetchConfig() {
    const { data } = await axios.get('/canal/whatsapp/config');
    return data; // { app_id, config_id, graph_version, configurado }
  },

  /**
   * Inicializa o SDK com appId. Idempotente — chame antes do startEmbeddedSignup.
   */
  async init(appId, graphVersion = 'v20.0') {
    if (!appId) throw new Error('appId é obrigatório.');
    window.__metaAppId = appId;
    return carregarSdk(graphVersion);
  },

  /**
   * Dispara FB.login com o feature flag de Embedded Signup. Retorna o `code`
   * que deve ser enviado pro backend.
   *
   * Documentação da Meta:
   *   https://developers.facebook.com/docs/whatsapp/embedded-signup/implementation
   */
  startEmbeddedSignup(configId) {
    return new Promise((resolve, reject) => {
      if (!window.FB) return reject(new Error('SDK Meta não inicializado.'));
      if (!configId) return reject(new Error('Configuration ID ausente.'));

      const sessionInfoListener = (event) => {
        if (event.origin !== 'https://www.facebook.com' && event.origin !== 'https://web.facebook.com') return;
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'WA_EMBEDDED_SIGNUP') {
            window.__metaWaSessionInfo = data;
          }
        } catch (_e) { /* ignore non-JSON messages */ }
      };
      window.addEventListener('message', sessionInfoListener);

      window.FB.login(
        (response) => {
          window.removeEventListener('message', sessionInfoListener);
          if (response && response.authResponse && response.authResponse.code) {
            resolve({
              code: response.authResponse.code,
              sessionInfo: window.__metaWaSessionInfo || null,
            });
          } else {
            reject(new Error('Embedded Signup cancelado ou falhou.'));
          }
        },
        {
          // Formato v4 do Embedded Signup (atual). Versões anteriores
          // usavam extras.feature='whatsapp_embedded_signup' — agora basta
          // version:'v4' e o session info v3 é default.
          config_id: configId,
          response_type: 'code',
          override_default_response_type: true,
          extras: { version: 'v4' },
        },
      );
      return null;
    });
  },
};
